import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { IHistoryEntry } from '@platform/core/types';
import { displayNumber } from './displayNumber';

/** Значення-посилання (ідентифікатор запису чи користувача) — не для показу: тоді «оновив(-ла) «Поле»». */
const REF = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Опис події історії для «Нещодавньої активності» Головної (прототип v4): «створив(-ла) запис», «прокоментував(-ла)», «пов’язано з 9»
 * (інший кінець невідомий чи вилучений — «з іншим записом», як текст сервера),
 * «змінено «Поле» на «значення»» (значення статусу — підписом мовою інтерфейсу), «оновив(-ла) «Поле»» (і для значень-посилань).
 */
export function useActivityText(): (entry: IHistoryEntry, statusLabel: (value: string) => string) => string {
  const { t } = useTranslation();
  return useCallback((e: IHistoryEntry, statusLabel: (value: string) => string) => {
    const field = e.kind === 'status_changed' ? t('homePage.act.status') : e.fieldLabel ?? e.field ?? '';
    const isStatus = e.kind === 'status_changed' || e.field === 'status';
    switch (e.kind) {
      case 'created': return t('homePage.act.created');
      case 'comment': return t('homePage.act.commented');
      case 'linked': return e.otherDisplay ? t('homePage.act.linked', { other: displayNumber(e.otherDisplay) }) : t('homePage.act.linkedOther');
      case 'changed':
      case 'status_changed':
        return e.to != null && !REF.test(e.to) ? t('homePage.act.set', { field, value: isStatus ? statusLabel(e.to) : e.to }) : t('homePage.act.updated', { field });
      default: return t(`history.${e.kind}`, { defaultValue: t('homePage.act.updatedRecord') });
    }
  }, [t]);
}
